/**
 * OfflineIndicator Component
 * 
 * Shows visual indicator when app is offline and manages offline queue.
 */

import React, { useState, useEffect, memo } from 'react';
import PropTypes from 'prop-types';

const OfflineIndicator = memo(({ isOnline, queuedUpdates = 0 }) => {
  const [show, setShow] = useState(!isOnline);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (!isOnline) {
      setShow(true);
      setIsAnimating(true);
      setTimeout(() => setIsAnimating(false), 500);
    } else {
      // Hide after a brief delay when coming back online
      setTimeout(() => {
        setShow(false);
      }, 2000);
    }
  }, [isOnline]);

  if (!show) return null;

  return (
    <div
      className={`fixed top-4 left-1/2 transform -translate-x-1/2 z-[9999] transition-all duration-500 ${
        isAnimating ? 'animate-bounce' : ''
      } ${show ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-full'}`}
    >
      <div
        className={`px-4 py-2 rounded-full shadow-lg flex items-center gap-2 ${
          isOnline
            ? 'bg-green-500 text-white'
            : 'bg-yellow-500 text-yellow-900'
        }`}
      >
        <span className="text-xl">
          {isOnline ? '✅' : '📡'}
        </span>
        <span className="font-semibold text-sm">
          {isOnline
            ? 'Back Online! Syncing...'
            : queuedUpdates > 0
            ? `Offline - ${queuedUpdates} update${queuedUpdates > 1 ? 's' : ''} queued`
            : 'Offline Mode'}
        </span>
      </div>
    </div>
  );
});

OfflineIndicator.propTypes = {
  isOnline: PropTypes.bool.isRequired,
  queuedUpdates: PropTypes.number
};

OfflineIndicator.displayName = 'OfflineIndicator';

export default OfflineIndicator;
